import React, { useState, useEffect } from 'react';
import { loggingService, LogEntry, LogLevel } from '../services/loggingService';
import './LogViewer.css';

const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'ALL'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updateLogs = () => {
      setLogs(loggingService.getAllLogs());
    };

    updateLogs();

    if (autoRefresh) {
      const interval = setInterval(updateLogs, 1000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const filteredLogs = logs.filter(log => {
    if (levelFilter !== 'ALL' && log.level !== levelFilter) return false;
    if (categoryFilter !== 'ALL' && log.category !== categoryFilter) return false;
    if (filter && !log.message.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  const categories = Array.from(new Set(logs.map(log => log.category)));

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case LogLevel.ERROR: return '#ff4444';
      case LogLevel.WARN: return '#ffaa00';
      case LogLevel.INFO: return '#4488ff';
      case LogLevel.DEBUG: return '#888888';
      default: return '#ffffff';
    }
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="log-viewer-toggle"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        View Logs
      </button>
    );
  }

  return (
    <div className="log-viewer-overlay" onClick={() => setIsVisible(false)}>
      <div className="log-viewer-container" onClick={(e) => e.stopPropagation()}>
        <div className="log-viewer-header">
          <h2>📊 Chat Application Logs</h2>
          <div className="log-viewer-controls">
            <button onClick={() => loggingService.clearLogs()}>Clear</button>
            <button onClick={() => loggingService.downloadLogs()}>Download</button>
            <button onClick={() => {
              const json = loggingService.exportLogs();
              navigator.clipboard.writeText(json);
            }}>Copy JSON</button>
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            <button onClick={() => setIsVisible(false)}>Close</button>
          </div>
        </div>
        <div className="log-viewer-filters">
          <input
            type="text"
            placeholder="Filter by message..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="log-filter-input"
          />
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LogLevel | 'ALL')}
            className="log-filter-select"
          >
            <option value="ALL">All Levels</option>
            <option value={LogLevel.DEBUG}>DEBUG</option>
            <option value={LogLevel.INFO}>INFO</option>
            <option value={LogLevel.WARN}>WARN</option>
            <option value={LogLevel.ERROR}>ERROR</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="log-filter-select"
          >
            <option value="ALL">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <span className="log-count">Total: {logs.length} | Filtered: {filteredLogs.length}</span>
        </div>
        <div className="log-viewer-content">
          {filteredLogs.length === 0 ? (
            <div className="log-empty">No logs to display</div>
          ) : (
            filteredLogs.slice().reverse().map((log, index) => (
              <div key={index} className="log-entry" style={{ borderLeftColor: getLevelColor(log.level) }}>
                <div className="log-entry-header">
                  <span className="log-level" style={{ color: getLevelColor(log.level) }}>
                    [{log.level}]
                  </span>
                  <span className="log-category">[{log.category}]</span>
                  <span className="log-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                </div>
                <div className="log-message">{log.message}</div>
                {log.data && (
                  <details className="log-data">
                    <summary>Data</summary>
                    <pre>{JSON.stringify(log.data, null, 2)}</pre>
                  </details>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LogViewer;

